import { CircularProgress } from '@mui/material';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import React, { useCallback, useMemo } from 'react';
import { useIsUserAnalysisEnabled } from 'apps/sigma2-ui/src/components/ui/useUserAnalysis';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { mapCustomVizRequestToAnalyticsEventProperties } from 'apps/sigma2-ui/src/components/custom-viz/CustomVizHelpers';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { doCustomVizsEquate } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomVizDoViz';
import { useUserAnalysisSignaling } from 'apps/sigma2-ui/src/state/user-analysis';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export const CustomVizCircularSpinner: React.FC<{
  /**
   * Side effect for when the user taps the "Continue Exploring" button. It's
   * used for resetting the filters and metric bars to either show the last
   * successful viz request or an empty state if there isn't one
   * @see useOnUserAnalysisCancel
   */
  onUserAnalysisCancel?: (
    customVizRequestToCancel: Nullable<CustomVizRequest>,
  ) => void;
}> = ({ onUserAnalysisCancel }) => {
  const isEnabled = useIsUserAnalysisEnabled();

  const analytics = useAnalytics();

  const { currentUserAnalyses } = useUserAnalysisSignaling();

  const { featureFlags } = useUser();
  const language = useLanguage();

  const {
    currentInProgressCustomVizRequest,
    hasCurrentCustomVizPolledMultipleTimes,
  } = useCustomViz();

  const currentStep = useMemo(() => {
    return (
      currentUserAnalyses?.find((analysis) =>
        doCustomVizsEquate(
          analysis.customVizRequest,
          currentInProgressCustomVizRequest,
        ),
      )?.currentStep ?? null
    );
  }, [currentUserAnalyses, currentInProgressCustomVizRequest]);

  const onContinueExploringClick = useCallback(() => {
    if (currentInProgressCustomVizRequest && onUserAnalysisCancel) {
      analytics.trackEvent({
        eventName: 'Custom Viz Continue Exploring',
        eventProperties: mapCustomVizRequestToAnalyticsEventProperties({
          request: currentInProgressCustomVizRequest,
        }),
      });

      if (onUserAnalysisCancel) {
        // We pass the current request as the one to cancel so it stops the polling for the status
        onUserAnalysisCancel(currentInProgressCustomVizRequest);
      }
    }
  }, [onUserAnalysisCancel, analytics, currentInProgressCustomVizRequest]);

  return (
    <Spacing
      direction={'vertical'}
      size={'md'}
      className="items-center justify-center h-full"
    >
      <div data-testid="spinner">
        <CircularProgress size={76} className="text-secondary" />
      </div>
      {isEnabled &&
        hasCurrentCustomVizPolledMultipleTimes &&
        onUserAnalysisCancel && (
          <div className="mt-4">
            <button>
              <TouchTarget
                content={() => {
                  return (
                    <>
                      <S2Typography
                        variant="body-6"
                        textColorVariant={'secondary'}
                        translatedContent={language.continueExploring()}
                      />
                      <div className="w-full min-h-[.125rem] bg-secondary" />
                    </>
                  );
                }}
                behavior={{
                  type: 'onClick',
                  onClick: onContinueExploringClick,
                }}
              />
            </button>
          </div>
        )}
      {featureFlags?.showStatusForCustomVizLoading === true && currentStep && (
        <S2Typography
          variant={'headline-6-alt'}
          translatedContent={language.customVizProgressStep({
            step: currentStep,
          })}
        />
      )}
    </Spacing>
  );
};
